import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, Scissors } from 'lucide-react';
import { toast } from 'sonner';
import { purchaseAPI } from '../lib/api';

const defaultPieces = (c) =>
  c.expected_pieces != null ? c.expected_pieces : (Number(c.total_strips || 0) * Number(c.blanks_per_strip || 0));

// Reusable "Reconcile to CTS" dialog: converts on-hand full-sheet stock into cut CTS pieces + scrap.
export const CTSReconcileDialog = ({ line, open, onClose, onDone }) => {
  const [submitting, setSubmitting] = useState(false);
  const [godownId, setGodownId] = useState('');
  const [fullSheets, setFullSheets] = useState('');
  const [scrapKg, setScrapKg] = useState('');
  const [reference, setReference] = useState('');
  const [lines, setLines] = useState([]);
  const [ctsOptions, setCtsOptions] = useState([]);

  useEffect(() => {
    if (!open) return;
    purchaseAPI.listCTSItemOptions()
      .then(res => setCtsOptions(res.data?.options || []))
      .catch(() => setCtsOptions([]));
  }, [open]);

  useEffect(() => {
    if (!line) return;
    const gws = line.godowns_with_stock || [];
    const sheets = Number(line.pending_sheets || line.received_sheets || 0);
    setGodownId(gws.length ? gws[0].godown_id : '');
    setFullSheets(String(sheets || ''));
    setScrapKg(line.cts_total_scrap_kg != null ? String(line.cts_total_scrap_kg) : '');
    setReference('');
    setLines((line.cts_instructions || []).map(c => {
      const perSheet = c.per_sheet != null ? Number(c.per_sheet) : null;
      // Auto-fill from Cutting Plan: sheets × pieces-per-sheet; else fall back to PO plan pieces.
      const pieces = perSheet != null && sheets > 0 ? Math.round(perSheet * sheets) : defaultPieces(c);
      return {
        cts_item_id: c.cts_item_id || '',
        cts_item_name: c.cts_item_name || '',
        cts_item_unique_id: c.cts_item_unique_id || '',
        cts_size: c.cts_size || '',
        per_sheet: perSheet,
        pieces_received: String(pieces || ''),
      };
    }));
  }, [line]);

  // Recompute auto-filled pieces whenever the number of full sheets changes.
  useEffect(() => {
    const s = parseFloat(fullSheets) || 0;
    setLines(prev => prev.map(l => l.per_sheet != null
      ? { ...l, pieces_received: String(Math.round(l.per_sheet * s)) }
      : l));
  }, [fullSheets]);

  if (!line) return null;

  const gws = line.godowns_with_stock || [];
  const selectedGodown = gws.find(g => g.godown_id === godownId);
  const isInboundStaging = /inbound\s*staging/i.test(selectedGodown?.godown_name || '');
  const anyUnlinked = lines.some(l => !l.cts_item_id);

  const submit = async () => {
    if (!godownId) { toast.error('Select the godown holding the full sheets'); return; }
    if (!(parseFloat(fullSheets) > 0)) { toast.error('Enter the number of full sheets to convert'); return; }
    if (anyUnlinked) { toast.error('A cut size has no linked CTS item — link it in the Cutting Plan (BOM) first'); return; }
    setSubmitting(true);
    try {
      const res = await purchaseAPI.reconcileCTS(line.pr_id, {
        item_id: line.item_id,
        source_godown_id: godownId,
        full_sheets: parseFloat(fullSheets) || 0,
        cts_lines: lines.map(l => ({
          cts_item_id: l.cts_item_id,
          cts_size: l.cts_size,
          pieces_received: parseFloat(l.pieces_received) || 0,
        })),
        scrap_kg: scrapKg === '' ? null : parseFloat(scrapKg),
        reference_number: reference,
      });
      toast.success(res.data?.message || 'Reconciled to CTS — cut pieces + scrap stocked');
      onDone && onDone();
      onClose && onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reconcile');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="max-w-lg" data-testid="cts-reconcile-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Scissors className="w-4 h-4 text-amber-600" /> Reconcile to CTS</DialogTitle>
          <DialogDescription>
            Convert full-sheet stock of <strong>{line.item_name}</strong> ({line.item_unique_id}) into cut pieces + scrap.
            PO {line.po_number}. Received {line.received_sheets}, already reconciled {line.reconciled_sheets}, pending {line.pending_sheets}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div>
            <Label className="text-xs">Full sheets are stocked in</Label>
            {gws.length === 0 ? (
              <div className="text-xs text-rose-600" data-testid="cts-reconcile-no-stock">No on-hand full-sheet stock found for this item.</div>
            ) : (
              <Select value={godownId} onValueChange={setGodownId}>
                <SelectTrigger className="h-8 text-xs" data-testid="cts-reconcile-godown"><SelectValue placeholder="Select godown" /></SelectTrigger>
                <SelectContent>
                  {gws.map(g => (
                    <SelectItem key={g.godown_id} value={g.godown_id}>{g.godown_name} — {g.quantity} on hand</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {isInboundStaging && (
              <div className="mt-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="cts-reconcile-qc-hint">
                These sheets are still in <strong>Inbound Staging</strong> awaiting QC. The cut pieces will be stocked into Inbound Staging as pending QC (per size) — clear them in <strong>Receipt QC</strong> to move them to Stores.
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Full sheets to convert (issue out)</Label>
              <Input type="number" step="any" value={fullSheets} onChange={e => setFullSheets(e.target.value)}
                className="h-8 text-xs" data-testid="cts-reconcile-sheets" />
              {selectedGodown && parseFloat(fullSheets) > selectedGodown.quantity && (
                <div className="text-[10px] text-rose-600 mt-0.5">Exceeds on-hand ({selectedGodown.quantity})</div>
              )}
            </div>
            <div>
              <Label className="text-xs">Scrap produced (Kg → SCRAP)</Label>
              <Input type="number" step="any" value={scrapKg} onChange={e => setScrapKg(e.target.value)}
                className="h-8 text-xs" data-testid="cts-reconcile-scrap" />
            </div>
          </div>

          <div>
            <Label className="text-xs">Cut pieces produced ({isInboundStaging ? 'held in Inbound Staging, pending QC' : 'stocked per size to STORE'})</Label>
            <div className="border rounded divide-y mt-1">
              {lines.length === 0 && <div className="p-2 text-xs text-muted-foreground">No cutting instructions on this line.</div>}
              {lines.map((l, i) => (
                <div key={i} className="flex items-start gap-2 p-1.5">
                  <div className="flex-1 space-y-1">
                    <div className="text-xs font-medium">{l.cts_size} mm</div>
                    <Select value={l.cts_item_id || '_none'}
                      onValueChange={(v) => {
                        const opt = ctsOptions.find(o => o.id === v);
                        setLines(prev => prev.map((x, xi) => xi === i ? {
                          ...x,
                          cts_item_id: v === '_none' ? '' : v,
                          cts_item_name: opt?.item_name || '',
                          cts_item_unique_id: opt?.unique_id || '',
                        } : x));
                      }}>
                      <SelectTrigger className="h-7 text-[11px]" data-testid={`cts-reconcile-item-${i}`}>
                        <SelectValue placeholder="Select CTS item" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">— Not linked —</SelectItem>
                        {ctsOptions.map(o => (
                          <SelectItem key={o.id} value={o.id}>{o.item_name} ({o.unique_id})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {!l.cts_item_id && <div className="text-[10px] text-rose-600">⚠ Pick the CTS item this size is stocked as</div>}
                  </div>
                  <div className="pt-5">
                    <Input type="number" step="any" value={l.pieces_received}
                      onChange={e => setLines(prev => prev.map((x, xi) => xi === i ? { ...x, pieces_received: e.target.value } : x))}
                      className="h-8 w-24 text-xs" data-testid={`cts-reconcile-pieces-${i}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs">Reference (optional)</Label>
            <Input value={reference} onChange={e => setReference(e.target.value)} className="h-8 text-xs"
              placeholder={line.po_number} data-testid="cts-reconcile-ref" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="cts-reconcile-cancel">Cancel</Button>
          <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={submit}
            disabled={submitting || gws.length === 0} data-testid="cts-reconcile-submit">
            {submitting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Scissors className="w-3 h-3 mr-1" />}
            Convert to CTS
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CTSReconcileDialog;
